import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MousePointer2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, errMsg } from "@/lib/api";
import { CONDITIONS, fmtDate } from "@/lib/constants";
import { ToothGraphic } from "./ToothGraphic";
import { UPPER, LOWER, toothName, SURFACE_LABELS } from "./teeth";

const EMPTY = { condition: "higido", surfaces: {}, notes: "" };

const BrushBar = ({ brush, setBrush }) => (
  <div className="flex flex-wrap gap-2" data-testid="odontogram-brushes">
    <button onClick={() => setBrush("select")} data-testid="brush-select"
      className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${brush === "select" ? "border-slate-800 bg-slate-800 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
      <MousePointer2 className="h-3.5 w-3.5" /> Selecionar
    </button>
    {Object.entries(CONDITIONS).map(([k, c]) => (
      <button key={k} onClick={() => setBrush(k)} data-testid={`brush-${k}`}
        className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${brush === k ? "border-sky-500 bg-sky-50 text-sky-800 ring-2 ring-sky-200" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
        <span className="h-3 w-3 rounded-full border border-slate-300" style={{ background: c.color }} /> {c.label}
      </button>
    ))}
  </div>
);

const ToothPanel = ({ n, data, treatments, onChange }) => {
  const [notes, setNotes] = useState(data.notes || "");
  useEffect(() => { setNotes(data.notes || ""); }, [n, data.notes]);
  if (!n) {
    return <p className="text-sm text-slate-400" data-testid="tooth-panel-empty">Clique em um dente para ver detalhes, ou escolha uma condição acima e clique no dente/face para marcar.</p>;
  }
  const related = treatments.filter((t) => (t.teeth || []).includes(String(n)));
  const surfaces = Object.entries(data.surfaces || {});
  return (
    <div className="space-y-4" data-testid="tooth-panel">
      <div>
        <p className="font-mono-num text-xs font-semibold text-sky-600">DENTE {n}</p>
        <p className="font-display text-lg font-bold text-slate-900">{toothName(n)}</p>
      </div>
      <div className="grid gap-1.5">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Condição geral</p>
        <Select value={data.condition} onValueChange={(v) => onChange({ ...data, condition: v })}>
          <SelectTrigger data-testid="tooth-condition-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(CONDITIONS).map(([k, c]) => <SelectItem key={k} value={k}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-slate-400">Faces marcadas</p>
        {surfaces.length ? (
          <div className="flex flex-wrap gap-1.5">
            {surfaces.map(([s, c]) => (
              <span key={s} className="rounded-full border px-2 py-0.5 text-xs" style={{ borderColor: CONDITIONS[c]?.color }}>
                {SURFACE_LABELS[s]}: {CONDITIONS[c]?.label}
              </span>
            ))}
          </div>
        ) : <p className="text-xs text-slate-400">Nenhuma face marcada</p>}
      </div>
      <div className="grid gap-1.5">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Anotações do dente</p>
        <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="tooth-notes-input" />
        <Button size="sm" variant="outline" onClick={() => onChange({ ...data, notes })} data-testid="tooth-notes-save-btn">Salvar anotação</Button>
      </div>
      <div>
        <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-slate-400">Histórico deste dente</p>
        {related.length ? related.map((t) => (
          <div key={t.id} className="border-l-2 border-sky-200 py-1 pl-3 text-sm">
            <p className="font-semibold text-slate-700">{fmtDate(t.date)} · {t.category}</p>
            <p className="text-slate-500">{t.description}</p>
          </div>
        )) : <p className="text-xs text-slate-400">Sem procedimentos registrados</p>}
      </div>
    </div>
  );
};

export default function Odontogram({ patientId, initial, treatments = [] }) {
  const api = useApi();
  const [teeth, setTeeth] = useState(initial || {});
  const [brush, setBrush] = useState("select");
  const [selected, setSelected] = useState(null);

  useEffect(() => { setTeeth(initial || {}); }, [initial]);

  const get = (n) => ({ ...EMPTY, ...(teeth[String(n)] || {}) });
  const save = async (n, data) => {
    setTeeth((t) => ({ ...t, [String(n)]: data }));
    try { await api.put(`/patients/${patientId}/teeth/${n}`, data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const onTooth = (n) => {
    setSelected(n);
    if (brush === "select") return;
    const cur = get(n);
    save(n, { ...cur, condition: cur.condition === brush ? "higido" : brush });
  };
  const onSurface = (n, s) => {
    setSelected(n);
    if (brush === "select") return;
    const cur = get(n);
    const surfaces = { ...cur.surfaces };
    if (surfaces[s] === brush || brush === "higido") delete surfaces[s];
    else surfaces[s] = brush;
    save(n, { ...cur, surfaces });
  };

  const counts = {};
  Object.values(teeth).forEach((t) => { if (t.condition && t.condition !== "higido") counts[t.condition] = (counts[t.condition] || 0) + 1; });
  const arch = (list, upper) => (
    <div className="flex justify-center gap-0.5">
      {list.map((n, i) => (
        <div key={n} className={`flex ${i === 7 ? "mr-3 border-r border-dashed border-sky-200 pr-2.5" : ""}`}>
          <ToothGraphic n={n} upper={upper} data={get(n)} selected={selected === n} onTooth={onTooth} onSurface={onSurface} />
        </div>
      ))}
    </div>
  );

  return (
    <div className="grid gap-5 xl:grid-cols-[1fr_300px]" data-testid="odontogram">
      <div className="surface p-5">
        <BrushBar brush={brush} setBrush={setBrush} />
        <div className="mt-6 overflow-x-auto pb-2">
          <div className="mx-auto w-max">
            <p className="mb-1 text-center text-xs font-bold uppercase tracking-[0.2em] text-sky-700/60">Arcada superior</p>
            {arch(UPPER, true)}
            <div className="my-3 flex items-center gap-3 text-[10px] font-semibold uppercase tracking-widest text-slate-300">
              <span className="h-px flex-1 bg-slate-200" /> Direito do paciente · Esquerdo do paciente <span className="h-px flex-1 bg-slate-200" />
            </div>
            {arch(LOWER, false)}
            <p className="mt-1 text-center text-xs font-bold uppercase tracking-[0.2em] text-sky-700/60">Arcada inferior</p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-3 border-t pt-4 text-xs text-slate-500" data-testid="odontogram-summary">
          {Object.keys(counts).length ? Object.entries(counts).map(([k, v]) => (
            <span key={k} className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: CONDITIONS[k]?.color }} />{CONDITIONS[k]?.label}: <b className="text-slate-700">{v}</b></span>
          )) : <span>Todos os dentes hígidos</span>}
        </div>
      </div>
      <div className="surface p-5">
        <ToothPanel n={selected} data={selected ? get(selected) : EMPTY} treatments={treatments} onChange={(d) => save(selected, d)} />
      </div>
    </div>
  );
}
